import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Trash2,
  Loader2,
  Cpu,
  MemoryStick,
  HardDrive,
  Pencil,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  Package,
} from 'lucide-react';
import { CurrencySelect } from '@/components/common/CurrencySelect';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HetznerApiPrice {
  location: string;
  price_hourly_net: string | null;
  price_hourly_gross: string | null;
  price_monthly_net: string | null;
  price_monthly_gross: string | null;
}

interface ApiServerType {
  name: string;
  description: string;
  cores: number;
  memory: number;
  disk: number;
  cpu_type: string;
  architecture: string;
  prices: HetznerApiPrice[];
}

interface ApiLocation {
  name: string;
  description: string;
  city: string;
  country: string;
  network_zone: string;
}

interface CatalogData {
  server_types: ApiServerType[];
  locations: ApiLocation[];
}

interface PriceEntry {
  id: string;
  effective_date: string;
  monthly_price: string;
  set_by: string;
  set_at: string | null;
  notes: string | null;
}

interface ServerConfig {
  id: string;
  hetzner_server_type: string;
  display_name: string;
  description: string | null;
  vcpus: number;
  memory_gb: number;
  disk_gb: number;
  is_active: boolean;
  sort_order: number;
  prices: Record<string, PriceEntry[]>;
  created_at: string;
  updated_at: string;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const QK_CONFIGS = ['hetzner-configs'];
const QK_CATALOG = ['hetzner-catalog'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCurrentPrice(prices: Record<string, PriceEntry[]>): { currency: string; amount: string } | null {
  const today = new Date().toISOString().slice(0, 10);
  for (const [currency, entries] of Object.entries(prices)) {
    // entries are sorted by -effective_date from the API
    const current = entries.find((e) => e.effective_date <= today);
    if (current) return { currency, amount: current.monthly_price };
  }
  return null;
}

export default function ServerConfigsPage() {
  const queryClient = useQueryClient();

  // ─── Fetch offerings (DB) ──────────────────────────────────────────
  const {
    data: configs,
    isLoading: configsLoading,
  } = useQuery<ServerConfig[]>({
    queryKey: QK_CONFIGS,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/hetzner-configs/');
      return res.data;
    },
  });

  // ─── Fetch catalog (Hetzner API data for dropdowns / lookups) ──────
  const {
    data: catalog,
    error: catalogError,
  } = useQuery<CatalogData>({
    queryKey: QK_CATALOG,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/hetzner-catalog/');
      return res.data;
    },
  });

  const isApiKeyMissing =
    catalogError && (catalogError as any)?.response?.status === 503;

  // Build lookup maps from catalog
  const serverTypeMap = useMemo(() => {
    const map = new Map<string, ApiServerType>();
    catalog?.server_types.forEach((st) => map.set(st.name, st));
    return map;
  }, [catalog]);

  // ─── Refresh catalog ───────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/hetzner-catalog/?refresh=true');
      queryClient.setQueryData(QK_CATALOG, res.data);
      toast.success('Catalog refreshed from Hetzner API');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  // ─── Toggle active mutation ────────────────────────────────────────
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/hetzner-configs/${id}/`, { is_active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  // ─── Delete offering mutation ──────────────────────────────────────
  const deleteConfigMutation = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/hetzner-configs/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      toast.success('Offering deleted');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  // ─── Add Offering dialog ──────────────────────────────────────────
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({
    server_type: '',
    display_name: '',
    // initial price
    currency: 'EUR',
    monthly_price: '',
    effective_date: new Date().toISOString().slice(0, 10),
  });

  const selectedApiType = serverTypeMap.get(addForm.server_type);

  const createOfferingMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      // Step 1: Create the config
      const configRes = await api.post('/global-admin/hetzner-configs/', {
        hetzner_server_type: addForm.server_type,
        display_name: addForm.display_name,
        vcpus: selectedApiType?.cores ?? 0,
        memory_gb: selectedApiType ? Math.round(selectedApiType.memory) : 0,
        disk_gb: selectedApiType?.disk ?? 0,
        is_active: true,
        sort_order: 0,
      });

      // Step 2: Create initial price
      await api.post('/global-admin/hetzner-prices/', {
        server_config: configRes.data.id,
        currency: addForm.currency,
        monthly_price: addForm.monthly_price,
        effective_date: addForm.effective_date,
        set_by: 'admin',
        notes: 'Initial price',
      });

      return configRes.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      setShowAddDialog(false);
      toast.success('Offering created');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  const openAddDialog = () => {
    setAddForm({
      server_type: '',
      display_name: '',
      currency: 'EUR',
      monthly_price: '',
      effective_date: new Date().toISOString().slice(0, 10),
    });
    setShowAddDialog(true);
  };

  // Auto-suggest display name when server type changes
  const handleServerTypeChange = (value: string) => {
    const apiType = serverTypeMap.get(value);
    setAddForm((prev) => ({
      ...prev,
      server_type: value,
      display_name: apiType?.description || value,
    }));
  };

  // ─── Edit Offering dialog ─────────────────────────────────────────
  const [editingConfig, setEditingConfig] = useState<ServerConfig | null>(null);
  const [editForm, setEditForm] = useState({
    display_name: '',
    description: '',
    sort_order: '0',
    is_active: true,
  });

  const editConfigMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      display_name: string;
      description: string;
      sort_order: number;
      is_active: boolean;
    }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/hetzner-configs/${data.id}/`, {
        display_name: data.display_name,
        description: data.description,
        sort_order: data.sort_order,
        is_active: data.is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      setEditingConfig(null);
      toast.success('Offering updated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  const openEditDialog = (config: ServerConfig) => {
    setEditingConfig(config);
    setEditForm({
      display_name: config.display_name,
      description: config.description || '',
      sort_order: String(config.sort_order),
      is_active: config.is_active,
    });
  };

  // ─── Manage Prices dialog ─────────────────────────────────────────
  const [pricingConfig, setPricingConfig] = useState<ServerConfig | null>(null);
  const [priceForm, setPriceForm] = useState({
    currency: 'EUR',
    monthly_price: '',
    effective_date: '',
    notes: '',
  });

  const addPriceMutation = useMutation({
    mutationFn: async (data: {
      server_config: string;
      currency: string;
      monthly_price: string;
      effective_date: string;
      notes: string;
    }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/global-admin/hetzner-prices/', {
        ...data,
        set_by: 'admin',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      setPriceForm({ currency: 'EUR', monthly_price: '', effective_date: '', notes: '' });
      toast.success('Price added');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  const deletePriceMutation = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/hetzner-prices/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      toast.success('Price deleted');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  const openPricingDialog = (config: ServerConfig) => {
    setPricingConfig(config);
    setPriceForm({ currency: 'EUR', monthly_price: '', effective_date: '', notes: '' });
  };

  // Keep pricingConfig in sync with latest configs data
  const currentPricingConfig = useMemo(() => {
    if (!pricingConfig || !configs) return pricingConfig;
    return configs.find((c) => c.id === pricingConfig.id) ?? pricingConfig;
  }, [pricingConfig, configs]);

  // ─── Loading state ────────────────────────────────────────────────
  if (configsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Server Offerings</h2>
          <p className="text-muted-foreground">
            Manage Hetzner Cloud server offerings with custom pricing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh API Data
          </Button>
          <Button onClick={openAddDialog} disabled={!catalog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Offering
          </Button>
        </div>
      </div>

      {isApiKeyMissing && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-3">
            <AlertTriangle className="h-10 w-10 text-amber-500" />
            <div className="text-center space-y-1">
              <h3 className="font-semibold">Hetzner API Key Not Configured</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                API data (server types, locations, Hetzner pricing) is unavailable.
                Existing offerings still work. Add your Hetzner API key in <strong>API Keys</strong> settings.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Offerings table ──────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Offerings
            <Badge variant="secondary" className="ml-1">
              {configs?.filter((c) => c.is_active).length ?? 0} active
            </Badge>
          </CardTitle>
          <CardDescription>
            Each offering defines a server type with custom pricing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Active</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>Server Type</TableHead>
                <TableHead className="text-right">vCPUs</TableHead>
                <TableHead className="text-right">RAM</TableHead>
                <TableHead className="text-right">Disk</TableHead>
                <TableHead className="text-right">Our Price</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!configs || configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No offerings yet. Click "Add Offering" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => {
                  const apiType = serverTypeMap.get(config.hetzner_server_type);
                  const currentPrice = getCurrentPrice(config.prices);
                  const isToggling =
                    toggleActiveMutation.isPending &&
                    (toggleActiveMutation.variables as any)?.id === config.id;

                  return (
                    <TableRow key={config.id} className={config.is_active ? '' : 'opacity-60'}>
                      <TableCell>
                        {isToggling ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Switch
                            checked={config.is_active}
                            onCheckedChange={(checked) =>
                              toggleActiveMutation.mutate({ id: config.id, is_active: checked })
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{config.display_name}</div>
                        {config.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {config.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{config.hetzner_server_type}</div>
                        {apiType && (
                          <Badge
                            variant={apiType.cpu_type === 'dedicated' ? 'default' : 'outline'}
                            className="text-xs mt-0.5"
                          >
                            {apiType.cpu_type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1 text-sm">
                          <Cpu className="h-3 w-3 text-muted-foreground" />
                          {config.vcpus}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1 text-sm">
                          <MemoryStick className="h-3 w-3 text-muted-foreground" />
                          {config.memory_gb} GB
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1 text-sm">
                          <HardDrive className="h-3 w-3 text-muted-foreground" />
                          {config.disk_gb} GB
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {currentPrice
                          ? `${currentPrice.amount} ${currentPrice.currency}/mo`
                          : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Edit offering"
                            onClick={() => openEditDialog(config)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Manage prices"
                            onClick={() => openPricingDialog(config)}
                          >
                            <DollarSign className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            title="Delete offering"
                            onClick={() => {
                              if (confirm(`Delete offering "${config.display_name}"?`)) {
                                deleteConfigMutation.mutate(config.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ─── Add Offering Dialog ──────────────────────────────────────── */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Offering</DialogTitle>
            <DialogDescription>
              Create a new server offering by selecting a Hetzner server type.
            </DialogDescription>
          </DialogHeader>
          {!catalog ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Server Type</Label>
                <Select value={addForm.server_type} onValueChange={handleServerTypeChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a server type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.server_types.map((st) => (
                      <SelectItem key={st.name} value={st.name}>
                        <span className="font-mono">{st.name}</span>
                        <span className="text-muted-foreground ml-2">
                          {st.cores}v / {st.memory}GB / {st.disk}GB
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  placeholder="e.g. Standard 8GB"
                  value={addForm.display_name}
                  onChange={(e) => setAddForm((f) => ({ ...f, display_name: e.target.value }))}
                />
              </div>

              {selectedApiType && (
                <div className="rounded-md border p-3 space-y-1 text-sm">
                  <div className="font-medium text-muted-foreground">Auto-populated specs:</div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Cpu className="h-3.5 w-3.5" /> {selectedApiType.cores} vCPUs
                    </span>
                    <span className="flex items-center gap-1">
                      <MemoryStick className="h-3.5 w-3.5" /> {selectedApiType.memory} GB RAM
                    </span>
                    <span className="flex items-center gap-1">
                      <HardDrive className="h-3.5 w-3.5" /> {selectedApiType.disk} GB Disk
                    </span>
                  </div>
                </div>
              )}

              <div className="border-t pt-4 space-y-3">
                <div className="font-medium text-sm">Initial Price</div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Currency</Label>
                    <CurrencySelect
                      value={addForm.currency}
                      onValueChange={(v) => setAddForm((f) => ({ ...f, currency: v }))}
                      className="w-full"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Monthly Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="29.99"
                        value={addForm.monthly_price}
                        onChange={(e) => setAddForm((f) => ({ ...f, monthly_price: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Effective Date</Label>
                      <Input
                        type="date"
                        value={addForm.effective_date}
                        onChange={(e) => setAddForm((f) => ({ ...f, effective_date: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={() => createOfferingMutation.mutate()}
              disabled={
                createOfferingMutation.isPending ||
                !addForm.server_type ||
                !addForm.display_name ||
                !addForm.currency ||
                !addForm.monthly_price ||
                !addForm.effective_date
              }
            >
              {createOfferingMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Offering
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Offering Dialog ─────────────────────────────────────── */}
      <Dialog open={editingConfig !== null} onOpenChange={(open) => !open && setEditingConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Offering</DialogTitle>
            <DialogDescription>
              Edit display settings for this offering. Server type cannot be changed.
            </DialogDescription>
          </DialogHeader>
          {editingConfig && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Server Type:</span>
                  <span className="font-mono font-medium">{editingConfig.hetzner_server_type}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Optional description..."
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={editForm.sort_order}
                    onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="edit-is-active"
                    checked={editForm.is_active}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                  />
                  <Label htmlFor="edit-is-active">Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConfig(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!editingConfig) return;
                editConfigMutation.mutate({
                  id: editingConfig.id,
                  display_name: editForm.display_name,
                  description: editForm.description,
                  sort_order: parseInt(editForm.sort_order) || 0,
                  is_active: editForm.is_active,
                });
              }}
              disabled={editConfigMutation.isPending}
            >
              {editConfigMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Manage Prices Dialog ─────────────────────────────────────── */}
      <Dialog open={pricingConfig !== null} onOpenChange={(open) => !open && setPricingConfig(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Prices</DialogTitle>
            <DialogDescription>
              {currentPricingConfig && (
                <>
                  Prices for <strong>{currentPricingConfig.display_name}</strong>{' '}
                  ({currentPricingConfig.hetzner_server_type})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {currentPricingConfig && (
            <div className="space-y-4">
              {/* Existing prices */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">Monthly Price</TableHead>
                    <TableHead>Effective Date</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(currentPricingConfig.prices).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                        No prices set yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(currentPricingConfig.prices).flatMap(([currency, entries]) =>
                      entries.map((entry) => {
                        const today = new Date().toISOString().slice(0, 10);
                        const isFuture = entry.effective_date > today;
                        const isCurrent = !isFuture && entries.indexOf(entry) === entries.findIndex((e) => e.effective_date <= today);
                        return (
                          <TableRow key={entry.id}>
                            <TableCell>
                              <Badge variant="outline">{currency}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {entry.monthly_price}
                              {isFuture && (
                                <Badge variant="secondary" className="ml-2 text-xs">Scheduled</Badge>
                              )}
                              {isCurrent && (
                                <Badge variant="default" className="ml-2 text-xs">Current</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{entry.effective_date}</TableCell>
                            <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]">
                              {entry.notes || '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => deletePriceMutation.mutate(entry.id)}
                                disabled={deletePriceMutation.isPending}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )
                  )}
                </TableBody>
              </Table>

              {/* Add new price form */}
              <div className="border-t pt-4 space-y-3">
                <div className="font-medium text-sm">Add Price</div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Currency</Label>
                    <CurrencySelect
                      value={priceForm.currency}
                      onValueChange={(v) => setPriceForm({ ...priceForm, currency: v })}
                      className="w-full"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Monthly Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="29.99"
                        value={priceForm.monthly_price}
                        onChange={(e) => setPriceForm({ ...priceForm, monthly_price: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Effective Date</Label>
                      <Input
                        type="date"
                        value={priceForm.effective_date}
                        onChange={(e) => setPriceForm({ ...priceForm, effective_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Notes</Label>
                      <Input
                        placeholder="Optional"
                        value={priceForm.notes}
                        onChange={(e) => setPriceForm({ ...priceForm, notes: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    if (!currentPricingConfig) return;
                    addPriceMutation.mutate({
                      server_config: currentPricingConfig.id,
                      currency: priceForm.currency,
                      monthly_price: priceForm.monthly_price,
                      effective_date: priceForm.effective_date,
                      notes: priceForm.notes,
                    });
                  }}
                  disabled={
                    addPriceMutation.isPending ||
                    !priceForm.currency ||
                    !priceForm.monthly_price ||
                    !priceForm.effective_date
                  }
                >
                  {addPriceMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Plus className="h-4 w-4 mr-1" />
                  Add Price
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
